import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { reportAnalyticsEvent } from "../../utils/upgradePopup";

interface Props {
  onClose: () => void;
}

/**
 * "Start Free. Go Deeper with Pro." Shown once to a Free user, right after
 * their first entry into Astria. A discovery card, not a pricing table.
 */
const FirstSessionUpgradeCard: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const free = [
    t("astria.fsFree1", "Daily Flow"),
    t("astria.fsFree2", "Astrology Snapshots"),
    t("astria.fsFree3", "Life GPS Overview"),
    t("astria.fsFree4", "Companion Conversations"),
  ];
  const pro = [
    t("astria.fsPro1", "Current Season"),
    t("astria.fsPro2", "Growth Edge"),
    t("astria.fsPro3", "Identity Patterns"),
    t("astria.fsPro4", "Relationship Dynamics"),
    t("astria.fsPro5", "Full Readings"),
    t("astria.fsPro6", "Unlimited Conversations"),
  ];

  const unlock = () => {
    reportAnalyticsEvent("upsell_clicked", { trigger: "first_session", placement: "first_session_card" });
    onClose();
    navigate("/subscription");
  };

  const gold = "#f5d99a";
  const soft = "var(--astria-text-secondary,#c8bfd1)";
  const card: React.CSSProperties = { flex: "1 1 220px", borderRadius: 18, padding: "20px 18px", textAlign: "left", position: "relative" };
  const list = (items: string[]) => (
    <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 18px", display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((item) => (
        <li key={item} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
          <Check size={15} style={{ flexShrink: 0, color: gold }} aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-session-title"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto", background: "rgba(0,0,0,.65)", backdropFilter: "blur(4px)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 560, margin: "auto", borderRadius: 24, padding: "30px 22px 22px", textAlign: "center", background: "var(--astria-surface,#1a131f)", color: "var(--astria-text-primary,#f7f3fa)", border: "1px solid rgba(245,217,154,.35)", boxShadow: "0 16px 48px -12px rgba(167,139,255,.5)" }}
      >
        <h2 id="first-session-title" style={{ fontSize: 24, fontWeight: 700, margin: "0 0 10px", lineHeight: 1.25 }}>
          {t("astria.fsTitleA", "Start Free.")}
          <br />
          {t("astria.fsTitleB", "Go Deeper with Pro.")}
        </h2>
        <p style={{ margin: "0 0 22px", fontSize: 14, color: soft, lineHeight: 1.5 }}>
          {t("astria.fsSubA", "Free helps you see what's happening.")}
          <br />
          {t("astria.fsSubB", "Pro helps you understand why.")}
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
          <section style={{ ...card, border: "1px solid rgba(255,255,255,.14)" }} aria-label="Free">
            <h3 style={{ margin: 0, fontSize: 13, letterSpacing: ".08em", textTransform: "uppercase", color: soft }}>{t("astria.planFree", "Free")}</h3>
            <p style={{ margin: "6px 0 0", fontSize: 16, fontWeight: 600 }}>{t("astria.fsFreeLine", "See where you are.")}</p>
            {list(free)}
            <button type="button" disabled style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "1px solid rgba(255,255,255,.2)", background: "transparent", color: soft, fontWeight: 600, fontSize: 14, cursor: "default" }}>
              {t("astria.fsCurrent", "Current Plan")}
            </button>
          </section>

          <section style={{ ...card, border: `1px solid ${gold}`, boxShadow: "0 0 24px -8px rgba(245,217,154,.45)" }} aria-label="Astria Pro">
            <span style={{ position: "absolute", top: -11, right: 14, padding: "3px 10px", borderRadius: 999, background: gold, color: "#1a1233", fontSize: 10, fontWeight: 800, letterSpacing: ".08em" }}>
              {t("astria.fsBadge", "MOST POPULAR")}
            </span>
            <h3 style={{ margin: 0, fontSize: 13, letterSpacing: ".08em", textTransform: "uppercase", color: gold }}>{t("astria.fsProName", "Astria Pro")}</h3>
            <p style={{ margin: "6px 0 0", fontSize: 16, fontWeight: 600 }}>{t("astria.fsProLine", "Understand what's shaping it.")}</p>
            {list(pro)}
            <button type="button" onClick={unlock} style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: 0, cursor: "pointer", fontWeight: 700, fontSize: 15, background: "linear-gradient(135deg,#f5d99a,#e0b866)", color: "#1a1233" }}>
              {t("astria.fsCta", "Unlock Astria Pro")}
            </button>
          </section>
        </div>

        <button type="button" onClick={onClose} style={{ marginTop: 14, padding: "8px 12px", border: 0, background: "transparent", color: soft, cursor: "pointer", fontSize: 13 }}>
          {t("astria.fsLater", "Maybe Later")}
        </button>
      </div>
    </div>
  );
};

export default FirstSessionUpgradeCard;
